import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CommonDataService } from './common-data.service';
import { ErrorHandlerService } from './error-handler.service';
import { InteractionService } from './interaction.service';

export interface HeaderProps {
  title: string;
  hasBackIcon: boolean;
  hasSearch?: boolean;
  tooltip?: string;
  toggle?: {
    on: boolean;
    text: string;
    onChange: Function;
  };
  searchItems?: Array<{ label: string; id: number }> | undefined;
  backIconRoute?: string;
  currentPageStatus?: 'In Process' | 'Suspended' | 'Approved' | 'Unsuccessful' | 'Completed';
}

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  private commonDataService!: CommonDataService;
  private errorHandlerService!: ErrorHandlerService;
  private interaction!: InteractionService;
  private organisationListSubject = new BehaviorSubject<any[]>([]);
  organisationList$ = this.organisationListSubject.asObservable();

  orgWithOrgId: any;
  nspWithCommFlag: any;

  constructor(private injector: Injector) {}

  private sideBarStatus = new BehaviorSubject<boolean>(false);
  private contactUsDialogStatus = new BehaviorSubject<boolean>(false);


  private Note = new BehaviorSubject<any>({});

  private _headerDetails = new BehaviorSubject<HeaderProps>({
    title: '',
    hasBackIcon: false,
    backIconRoute: undefined,
    hasSearch: false,
  });

  // Observable stream
  public lastSelectedCountry: any = null;
  public onClear: any = null;
  private searchItemSelectedSource = new BehaviorSubject<any>(this.lastSelectedCountry);
  private onSearchClearSource = new Subject<any>();
  searchItemSelected$ = this.searchItemSelectedSource.asObservable();
  onSearchClear$ = this.onSearchClearSource.asObservable();

  orgData: any;
  dropdown: any[];
  countries: any[] = [];
  orgWithPartnerType: {};
  multiFee: {};
  options: { label: any; id: number }[];

  // Emit the event
  emitSearchItemSelected(org: any) {
    if (!this.interaction) {
      this.interaction = this.injector.get(InteractionService);
    }

    const country = this.countries.find((o: any) => o.orgName === org.label);
    this.lastSelectedCountry = country; // Store the last selected country

    this.orgWithOrgId = country.partnerCode;
    this.nspWithCommFlag = country.nspWithCommFlag;
    this.interaction.setCountry(this.orgWithOrgId, this.multiFee[country.orgName], this.nspWithCommFlag);

    localStorage.setItem('partnerType', country.partnerType);
    localStorage.setItem('selectedPartner', country.partnerCode);
    localStorage.setItem('partnerCodeFromNavbar', this.orgWithOrgId);
    this.searchItemSelectedSource.next(country);
  }

  emitClearSearchTable() {
    this.onSearchClearSource.next(true);
  }

  headerDetails$ = this._headerDetails.asObservable();

  currentSideBarStatus = this.sideBarStatus.asObservable();
  currentContactUsDialogStatus = this.contactUsDialogStatus.asObservable();

  currentNote = this.Note.asObservable();

  updateHeaderDetails(details: Partial<HeaderProps>) {
    const currentHeaderDetails = this._headerDetails.value;

    this._headerDetails.next({
      ...currentHeaderDetails,
      ...details,
      backIconRoute: details.hasBackIcon ? details.backIconRoute : undefined,
    });
  }

  toggleSideBarStatus(newStatus = this.sideBarStatus.value) {
    this.sideBarStatus.next(!newStatus);
  }

  toggleContactUsDialogStatus(newStatus = this.contactUsDialogStatus.value) {
    newStatus ? this.unFreezeDocumentBody() : this.freezeDocumentBody();
    this.contactUsDialogStatus.next(!newStatus);
  }

  sendNote(content: any) {
    this.Note.next(content);
  }

  hideNote() {
    this.Note.next(false);
  }

  isMobile(): boolean {
    const userAgent = window.navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  }

  freezeDocumentBody() {
    document.body.setAttribute('class', 'overflow-hidden');
  }

  unFreezeDocumentBody() {
    document.body.setAttribute('class', 'overflow-auto');
  }

  initializeHeaderDetailsWithSearch(title: string) {
    // Lazy-inject services only when this function is called
    if (!this.commonDataService) {
      this.commonDataService = this.injector.get(CommonDataService);
    }
    if (!this.errorHandlerService) {
      this.errorHandlerService = this.injector.get(ErrorHandlerService);
    }

    this.organisationList$.subscribe((response: any) => {
      this.errorHandlerService.checkAndShowpageError(response.responseCode, 'login');

      const orgData = response|| [];
      const dropdown = [];
      const orgWithPartnerType = {};
      const multiFee = {};

      orgData.forEach((value) => {
        if (value.orgType.toLowerCase() !== 'regulator') {
          this.countries.push(value);
          dropdown.push(value.orgName);
          orgWithPartnerType[value.orgName] = value.partnerType;
          multiFee[value.orgName] = value.multiFee;
        }
      });

      const options = dropdown.map((item, index) => ({
        label: item,
        id: index,
      }));

      this.updateHeaderDetails({
        title,
        hasSearch: true,
        searchItems: options,
      });
      
       // Save these for future use if needed
      this.orgData = orgData;
      this.dropdown = dropdown;
      this.orgWithPartnerType = orgWithPartnerType;
      this.multiFee = multiFee;
      this.options = options;

    });
  }

  paginateTable(array: Array<any>, pageNumber: number, pageSize: number) {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }

  getTotalPagesNumber(array: Array<any>, pageSize: number): number {
    return Math.ceil(array.length / pageSize) || 1;
  }

  updateOrganisationList(organisationList: any[]): void {
    this.organisationListSubject.next(organisationList);
  }

  getOrganisationList(): any[] {
    return this.organisationListSubject.value;
  }
}
